import { Action } from '../../../store/state';
import { defaultUser } from '../../../store/User/store';
import { EditUserPayload } from '../../../store/User/types';
import { isPostalRequired } from '../../../utils/localisation_countries.utils';
import { formFields, defaultDetails, MIN_TOPICS_LENGTH } from './data';
import { InterestOption } from '../../common/Interests/types';
import { isPostalCodeValid } from './validators';

const enforceNumbers = (phone: string) => {
  const lastDigit = phone.slice(phone.length - 1);
  if (isNaN(parseInt(lastDigit))) return phone.substring(0, phone.length - 1);
  return phone;
};

export const interestsReducer = (interests: InterestOption[]): [] => {
  return interests.reduce((acc: [], curr: InterestOption) => {
    if (curr.active === true) acc.push(curr.value as never);
    return acc;
  }, []);
};

export const getUpdatedInterests = (
  updatedInterest: InterestOption,
  interests: InterestOption[]
) =>
  interests.map((interest: InterestOption) => {
    return interest.name === updatedInterest.name &&
      interest.value === updatedInterest.value
      ? {
          name: updatedInterest.name,
          value: updatedInterest.value,
          active: !updatedInterest.active,
          iconName: updatedInterest.iconName,
          translateKey: updatedInterest.translateKey,
        }
      : interest;
  });

export const generateDetailsPayload = (
  user: typeof defaultUser,
  interests: InterestOption[]
): typeof defaultDetails => {
  return {
    firstName: user.influencer_first_name,
    lastName: user.influencer_last_name,
    address1: user.influencer_address?.line_one ?? '',
    address2: user.influencer_address?.line_two ?? '',
    city: user.influencer_address?.city ?? '',
    postCode: user.influencer_address?.postcode ?? '',
    country: user.influencer_address?.country ?? '',
    phone: user.influencer_phone_number,
    interests: interests,
  };
};

export const checkDetailsValid = (
  details: typeof defaultDetails,
  tab: number
): boolean => {
  const {
    firstName,
    lastName,
    address1,
    address2,
    city,
    postCode,
    phone,
    country,
    interests,
  } = details;

  const detailsValid =
    (interests?.filter((x: InterestOption) => x.active === true).length >=
      MIN_TOPICS_LENGTH &&
      tab === 1) ||
    (firstName?.trim() !== '' &&
      lastName?.trim() !== '' &&
      address1?.trim() !== '' &&
      address1?.length <= 35 &&
      address2?.length <= 35 &&
      city?.trim() !== '' &&
      isPostalCodeValid(details.country, postCode) &&
      phone?.trim() !== '' &&
      country !== '' &&
      tab === 0);

  return detailsValid;
};

export const generateEditUserPayload = (
  state: typeof defaultDetails,
  user: typeof defaultUser
): EditUserPayload => {
  return {
    influencer_first_name: state.firstName,
    influencer_last_name: state.lastName,
    influencer_address: {
      line_one: state.address1,
      line_two: state.address2,
      city: state.city,
      postcode: isPostalRequired(state.country) ? state.postCode : '000 000',
      country: state.country,
    },
    influencer_phone_number: state.phone,
    influencer_interests: interestsReducer(state.interests),
    influencer_id: user.influencer_id,
    keycloak_id: user.keycloak_id,
    influencer_tours_viewed: user.influencer_tours_viewed,
    influencer_language: user.influencer_language,
  };
};

const detailsReducer = (
  state: typeof defaultDetails,
  action: Action
): typeof defaultDetails => {
  switch (action.type) {
    case formFields.FIRST_NAME:
      return { ...state, firstName: action.payload };
    case formFields.LAST_NAME:
      return { ...state, lastName: action.payload };
    case formFields.ADDRESS1:
      return { ...state, address1: action.payload };
    case formFields.ADDRESS2:
      return { ...state, address2: action.payload };
    case formFields.CITY:
      return { ...state, city: action.payload };
    case formFields.POST_CODE:
      return { ...state, postCode: action.payload };
    case formFields.PHONE_NUMBER:
      return {
        ...state,
        phone: enforceNumbers(action.payload),
      };
    case formFields.COUNTRY:
      return { ...state, country: action.payload };
    case formFields.INTERESTS:
      return {
        ...state,
        interests: getUpdatedInterests(action.payload, state.interests),
      };
    case 'RESET':
      return { ...action.payload };
    default:
      return state;
  }
};

export { detailsReducer };
